import React, { MutableRefObject, useEffect, useMemo, useRef, useState, useImperativeHandle, Ref } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import '../Message.css'
import { DeviceType } from 'models/DeviceType'
import { ButtonType, DropdownItemType, MessageType, RatingItemType } from 'models/MessageType'
import { SettingsType } from 'models/SettingsType'
import BubbleTail from 'components/BubbleTail'

import Button from '../TemplateMessage/Button'
import QuickReplyList from '../Reply/QuickReplyList'
import { generatePostBackObject, generatePostBackObjectWithType } from '../../../actions/static'
import MessageLogo from '../MessageLogo'
import { wrapTextAddLinks } from '../../../helper/wrapTextAddLinks'
import { openNewTab } from '../../../helper/openNewTab'
import { IframeSettingsType } from '../../../models/IframeSettingsType'
import { palette } from '../../../theme/palette'
import ConditionCards from '../ConditionCard/ConditionCards'
import DropdownSelect from '../../DropdownSelect'
import { buttonType, keyType } from '../../../constants/string'
import Rating from '../Rating/Rating'

const messageWidthText = (screenWidth, color, customStyles = {}, isRtl, showLogo, isFromBot) => ({
  backgroundColor: color ? color : '',
  direction: isRtl ? 'rtl' : 'ltr',
  borderRadius: getBorderRadius(showLogo, isFromBot),
  ...customStyles
} as React.CSSProperties)

const getBorderRadius = (showLogo, isFromBot) => {
  if (showLogo) return '14px 14px 14px 0px'
  if (!isFromBot) return '14px 14px 0px 14px'
  return '14px'
}

interface Props {
  classesNames: string
  postMessage: (a: MessageType) => void
  message: MessageType
  showReply: boolean
  showLogo: boolean
  device: DeviceType
  settings: SettingsType
  openIframe?: (a:string) => void
  isFullScreenWidget?: boolean
  messageMargin: string
  isMobile: boolean
  isEmpowerPre: boolean
  isEmpowerPost: boolean
  setIframe: ({}: any) => void
  isPanoramaPost: boolean
  isMaximized: boolean
  iframeSettings: IframeSettingsType
  avatarLogo: string
  setDatePickerSettings: (prevState: (prevState) => any) => void
  isLastMessageInCurrentFlow: boolean
  isImageLoaded: boolean
  setIsImageLoaded: (prevState: boolean) => void
  isLoadingIframeData: boolean
  messagesRef: MutableRefObject<HTMLElement[]>
  messageIndex: number
  updateMessagesState: (id: string, value: string | number, target: string) => void
  passedOrSkippedFlowComponentsId: string[]
  setPassedOrSkippedFlowComponentsId: (id: string) => void
  textMessageRef: Ref<{ resetRating: () => void }>
}

const TextMessage: React.FC<Props> = ({
  classesNames,
  postMessage,
  message,
  showReply,
  showLogo,
  device,
  settings,
  openIframe,
  isFullScreenWidget,
  messageMargin,
  isMobile,
  setIframe,
  isPanoramaPost,
  isEmpowerPre,
  isEmpowerPost,
  isMaximized,
  iframeSettings,
  avatarLogo,
  setDatePickerSettings,
  isLastMessageInCurrentFlow,
  isImageLoaded,
  setIsImageLoaded,
  isLoadingIframeData,
  messagesRef,
  messageIndex,
  updateMessagesState,
  passedOrSkippedFlowComponentsId,
  setPassedOrSkippedFlowComponentsId,
  textMessageRef
}) => {
  const output = useRef(null)
  const [dropdownItem, setDropdownItem] = useState<DropdownItemType>(null)
  const [selectedValue, setSelectedValue] = useState<string>(message.dropdown?.selectedValue || '')
  const [selectedRatingItem, setSelectedRatingItem] = useState<RatingItemType>(null)
  const [rating, setRating] = useState<number>(message.rating?.selectedValue || -1)
  const isEmpower = isEmpowerPre || isEmpowerPost
  const isDisabledComponentWithButton = useMemo(() => message.buttons?.some(button => passedOrSkippedFlowComponentsId.includes(button.id) || passedOrSkippedFlowComponentsId.includes(message.id)) || false, [message.buttons, message.id, passedOrSkippedFlowComponentsId])
  const { t } = useTranslation()
  useEffect(() => {
    linkEditorListener()
  })

  useImperativeHandle(textMessageRef, () => ({
    resetRating() {
      setRating(-1)
    },
    resetDropdown() {
      setSelectedValue('')
    }
  }))

  const linkEditorListener = () => {
    const links = output.current.querySelectorAll('.linkEdited')
    links.length > 0 &&
    links.forEach(link => {
      const extension = JSON.parse(link.getAttribute('data-extension'))
      const url = link.getAttribute('data-href')
      if (extension) {
        link.onkeydown = (e) => {
          const currentKey = e.key.toLowerCase()
          if (currentKey === keyType.ENTER || currentKey === keyType.SPACE) {
            e.preventDefault()
            setIframe({
              iframeSrc: url,
              isIframeOpen: true,
              questionnaireName: iframeSettings.questionnaireName
            })
          }
        }
        link.onclick = (e) => {
          e.preventDefault()
          setIframe({
            iframeSrc: url,
            isIframeOpen: true,
            questionnaireName: iframeSettings.questionnaireName
          })
        }
      } else {
        link.onkeydown = (e) => {
          const currentKey = e.key.toLowerCase()
          if (currentKey === keyType.ENTER || currentKey === keyType.SPACE) {
            e.preventDefault()
            openNewTab(url)
          }
        }
        link.onclick = e => {
          e.preventDefault()
          openNewTab(url)
        }
      }
    })
  }

  const sendCustomPostMessage = (button: ButtonType): void => {
    let attributePayload
    let type
    switch (button.type) {
      case buttonType.DROPDOWN:
        attributePayload = dropdownItem.attributePayload
        type = 'postback'
        break
      case buttonType.RATING:
        attributePayload = selectedRatingItem.attributePayload
        type = buttonType.RATING
        break
    }

    const customPostBackObject = {
      payload: button.payload.split(';?attributes=')[0] + attributePayload,
      ...(button.type === buttonType.DROPDOWN && { title: dropdownItem.option }),
      ...(button.type === buttonType.RATING && { title: selectedRatingItem.title })
    }

    postMessage(generatePostBackObjectWithType(customPostBackObject, type))
  }

  const onSelectHandler = (item: DropdownItemType): void => {
    setSelectedValue(item.option)
    setDropdownItem(item)
  }

  const onButtonClickHandler = (button: ButtonType): void => {
    switch (button.type) {
      case buttonType.POSTBACK:
        postMessage(generatePostBackObject(button))
        break
      case buttonType.DROPDOWN:
      case buttonType.RATING:
        const targetValue = button.type === buttonType.DROPDOWN ? 'dropdown' : 'rating'
        const itemValue = button.type === buttonType.DROPDOWN ? selectedValue : selectedRatingItem.scoreNumber
        sendCustomPostMessage(button)
        updateMessagesState(message.id, itemValue, targetValue)
        setPassedOrSkippedFlowComponentsId(button.id)
        break
    }
  }

  const containerClass =
    classesNames && ~classesNames.indexOf('bubble--sent')
      ? 'msg--sent'
      : 'msg--rcvd'
  const color =
    classesNames && ~classesNames.indexOf('bubble--sent')
      ? palette.sentMessage
      : palette.white

  const wrapClass = message.isFromBot && showLogo ? 'received-container_logo' : 'received-container'
  const dataTestValue = classesNames && ~classesNames.indexOf('bubble--sent') ? 'msg-sent' : 'msg-rcvd'

  return (
    <>
      {message.buttons != null ? (
        <div ref={output} className={wrapClass}>
          {message.isFromBot && showLogo && <MessageLogo avatarLogo={avatarLogo} />}
          <div
            className={'msg_container-button msg-animation'}
            style={{ borderRadius: getBorderRadius(showLogo, message.isFromBot) }}>
            <div
              data-test={dataTestValue}
              className={`msg_button ${containerClass}`}>
              <span
                ref={el => messagesRef.current[messageIndex] = el}
                className={classesNames}
                style={{ direction: message.isRtl ? 'rtl' : 'ltr' }}
                dangerouslySetInnerHTML={{
                  __html: wrapTextAddLinks(message.text, t('external_link_icon'), isEmpower, isLoadingIframeData)
                }}
              />
            </div>
            {message.dropdown &&
              <DropdownSelect
                items={message.dropdown.items}
                onSelect={onSelectHandler}
                isDisabled={isDisabledComponentWithButton}
                isLoadingIframeData={isLoadingIframeData}
                selectedValue={selectedValue}
              />
            }
            {message.rating &&
              <Rating
                items={message.rating.items}
                type={message.rating.type}
                rating={rating}
                setRating={setRating}
                onRatingSelect={setSelectedRatingItem}
                isDisabled={isDisabledComponentWithButton}
              />
            }
            {message.buttons &&
              message.buttons.map((button, index) => (
                <Button
                  button={button}
                  key={index}
                  title={button.title}
                  settings={settings}
                  openIframe={openIframe}
                  setIframe={setIframe}
                  isPanoramaPost={isPanoramaPost}
                  onClick={() => onButtonClickHandler(button)}
                  setDatePickerSettings={setDatePickerSettings}
                  dropdownItem={dropdownItem}
                  isDisabledComponentWithButton={isDisabledComponentWithButton}
                  isLoadingIframeData={isLoadingIframeData}
                  selectedRatingItem={selectedRatingItem}
                />
              ))
            }
            {(showLogo || !message.isFromBot) && <BubbleTail isFromBot={message.isFromBot} messageStyle={message.messageStyle}/>}
          </div>
        </div>
      ) : (
        <div
          ref={output}
          className={`msg-container ${containerClass} ${wrapClass}`}
          data-test={dataTestValue}
          style={{ margin: messageMargin }}>
          {message.isFromBot && showLogo && <MessageLogo avatarLogo={avatarLogo} />}
          <div className={'msg-animation'}>
            <div
              className={classNames(classesNames, { 'click-disable': iframeSettings.isIframeOpen })}
              style={messageWidthText(device.width, color, message.messageStyle, message.isRtl, showLogo, message.isFromBot)}
            >
              <div
                ref={el => messagesRef.current[messageIndex] = el}
                style={{ marginBottom: message.conditionCards ? '12px' : '0' }}
                dangerouslySetInnerHTML={{
                  __html: wrapTextAddLinks(message.text, t('external_link_icon'), isEmpower, isLoadingIframeData)
                }}
              />
              {message.conditionCards &&
                <ConditionCards
                  conditionCards={message.conditionCards}
                  postMessage={postMessage}
                  isLastMessageInCurrentFlow={isLastMessageInCurrentFlow}
                  isLoadingIframeData={isLoadingIframeData}
                />
              }
            </div>
            {(showLogo || !message.isFromBot) && <BubbleTail isFromBot={message.isFromBot} messageStyle={message.messageStyle}/>}
          </div>
        </div>
      )}
      {showReply && message && (
        <QuickReplyList
          isMobile={isMobile}
          message={message}
          replies={message.quickReplies || []}
          settings={settings}
          postMessage={postMessage}
          isFullScreenWidget={isFullScreenWidget}
          isMaximized={isMaximized}
          isImageLoaded={isImageLoaded}
          setIsImageLoaded={setIsImageLoaded}
          isLoadingIframeData={isLoadingIframeData}
          setSelectedValue={setSelectedValue}
          setPassedOrSkippedFlowComponentsId={setPassedOrSkippedFlowComponentsId}
          setRating={setRating}
        />
      )}
    </>
  )
}

export default TextMessage
